import { useState, useRef, useEffect, useCallback } from "react";
import { SavedRecipe, getRecipeTotals } from "@/hooks/useSavedRecipes";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { Trash2, ChevronDown, ChevronUp, Flame, Beef, Wheat, Droplets, Globe, User, Settings2, Pencil } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getIconComponent } from "@/components/recipes/RecipeIconPicker";

interface SavedRecipesListProps {
  recipes: SavedRecipe[];
  globalRecipes: SavedRecipe[];
  isAdmin: boolean;
  onDelete: (id: string) => Promise<void>;
  onUpdateVisibility?: (id: string, vis: { visible_basic: boolean; visible_plus: boolean; visible_complete: boolean }) => Promise<void>;
  onEdit?: (recipe: SavedRecipe) => void;
  loading: boolean;
}

const SavedRecipesList = ({ recipes, globalRecipes, isAdmin, onDelete, onUpdateVisibility, onEdit, loading }: SavedRecipesListProps) => {
  const { toast } = useToast();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await onDelete(id);
      toast({ title: "Receita excluída! 🗑️" });
    } catch (e: any) {
      toast({ title: "Erro ao excluir", description: e.message, variant: "destructive" });
    }
    setDeletingId(null);
  };

  const handleToggleVisibility = async (recipe: SavedRecipe, field: "visible_basic" | "visible_plus" | "visible_complete") => {
    if (!onUpdateVisibility) return;
    try {
      await onUpdateVisibility(recipe.id, {
        visible_basic: field === "visible_basic" ? !recipe.visible_basic : recipe.visible_basic,
        visible_plus: field === "visible_plus" ? !recipe.visible_plus : recipe.visible_plus,
        visible_complete: field === "visible_complete" ? !recipe.visible_complete : recipe.visible_complete,
      });
    } catch (e: any) {
      toast({ title: "Erro ao atualizar", description: e.message, variant: "destructive" });
    }
  };

  const renderRecipeCard = (recipe: SavedRecipe, isGlobal: boolean) => {
    const totals = getRecipeTotals(recipe);
    const isExpanded = expandedId === recipe.id;

    const CustomIcon = getIconComponent(recipe.icon);

    return (
      <motion.div
        key={recipe.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="border border-border rounded-xl overflow-hidden bg-background relative"
      >
        {/* Banner image background */}
        {recipe.banner_image_url && (
          <div className="absolute inset-0 z-0">
            <img src={recipe.banner_image_url} alt="" className="w-full h-full object-cover opacity-15" />
          </div>
        )}

        <div className="flex items-center gap-2 p-3 relative z-10">
          <button
            onClick={() => setExpandedId(isExpanded ? null : recipe.id)}
            className="flex-1 min-w-0 flex items-center gap-2 text-left"
          >
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center overflow-hidden shrink-0">
              {recipe.banner_image_url ? (
                <img src={recipe.banner_image_url} alt="" className="w-full h-full object-cover" />
              ) : CustomIcon ? (
                <CustomIcon className="w-4 h-4 text-primary" />
              ) : isGlobal ? (
                <Globe className="w-4 h-4 text-primary" />
              ) : (
                <User className="w-4 h-4 text-primary" />
              )}
            </div>
            <div className={`min-w-0 flex-1 overflow-hidden ${recipe.banner_image_url ? "bg-background/85 backdrop-blur-sm px-2 py-1 rounded-lg" : ""}`}>
              <ScrollableTitle name={recipe.name} />
              <div className="text-[11px] text-muted-foreground">
                <div className="text-primary font-medium">{totals.kcal.toFixed(0)} kcal</div>
                <div className="flex gap-2 whitespace-nowrap">
                  <span>{totals.protein.toFixed(0)}g P</span>
                  <span>{totals.carbs.toFixed(0)}g C</span>
                  <span>{totals.fat.toFixed(0)}g G</span>
                </div>
              </div>
            </div>
          </button>
          <div className={`flex items-center gap-0.5 shrink-0 ${recipe.banner_image_url ? "bg-background/85 backdrop-blur-sm rounded-lg px-1" : ""}`}>
            <button
              onClick={() => setExpandedId(isExpanded ? null : recipe.id)}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors shrink-0"
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {(!isGlobal || isAdmin) && (
              <>
                {onEdit && (
                  <button
                    onClick={() => onEdit(recipe)}
                    className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors shrink-0"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => handleDelete(recipe.id)}
                  disabled={deletingId === recipe.id}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="overflow-hidden">
              <div className={`px-3 pb-3 space-y-2 relative z-10 ${recipe.banner_image_url ? "mx-2 mb-2 bg-background/85 backdrop-blur-sm rounded-lg p-3" : ""}`}>
                {/* Ingredients */}
                {recipe.recipe_ingredients.map(ing => {
                  const factor = ing.quantity_grams / 100;
                  const food = ing.taco_foods;
                  return (
                    <div key={ing.id} className="flex items-center justify-between text-xs py-1 border-t border-border/50">
                      <div>
                        <span className="text-foreground font-medium">{food.description}</span>
                        <span className="text-muted-foreground ml-1">({ing.quantity_grams}g)</span>
                      </div>
                      <div className="flex gap-2 text-muted-foreground whitespace-nowrap">
                        <span>{((food.energy_kcal || 0) * factor).toFixed(0)} kcal</span>
                        <span>{((food.protein_g || 0) * factor).toFixed(1)}g P</span>
                      </div>
                    </div>
                  );
                })}

                {/* Totals */}
                <div className="grid grid-cols-4 gap-2 pt-2 border-t border-border">
                  <MiniMacro icon={Flame} value={totals.kcal.toFixed(0)} unit="kcal" color="text-primary" />
                  <MiniMacro icon={Beef} value={totals.protein.toFixed(1)} unit="proteínas" color="text-primary" />
                  <MiniMacro icon={Wheat} value={totals.carbs.toFixed(1)} unit="carboidratos" color="text-accent" />
                  <MiniMacro icon={Droplets} value={totals.fat.toFixed(1)} unit="gorduras" color="text-warning" />
                </div>

                {/* Admin visibility switches */}
                {isGlobal && isAdmin && (
                  <div className="pt-2 border-t border-border space-y-2">
                    <p className="text-xs font-semibold text-foreground flex items-center gap-1">
                      <Settings2 className="w-3.5 h-3.5" /> Visibilidade por plano
                    </p>
                    <div className="flex flex-wrap gap-4">
                      <VisSwitch label="PRO FIT" checked={recipe.visible_basic} onChange={() => handleToggleVisibility(recipe, "visible_basic")} />
                      <VisSwitch label="PRO FIT PLUS" checked={recipe.visible_plus} onChange={() => handleToggleVisibility(recipe, "visible_plus")} />
                      <VisSwitch label="PRO FIT MAX" checked={recipe.visible_complete} onChange={() => handleToggleVisibility(recipe, "visible_complete")} />
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-6">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-muted-foreground mt-2">Carregando receitas...</p>
      </div>
    );
  }

  const allRecipes = [...recipes];
  const hasAny = allRecipes.length > 0 || globalRecipes.length > 0;

  if (!hasAny) {
    return (
      <div className="text-center py-6">
        <p className="text-sm text-muted-foreground">Nenhuma receita salva ainda.</p>
        <p className="text-xs text-muted-foreground mt-1">Use a busca acima para criar sua primeira receita!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {allRecipes.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
            <User className="w-3.5 h-3.5" /> Minhas Receitas ({allRecipes.length})
          </p>
          {allRecipes.map(r => renderRecipeCard(r, false))}
        </div>
      )}

      {globalRecipes.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
            <Globe className="w-3.5 h-3.5" /> Receitas Disponíveis ({globalRecipes.length})
          </p>
          {globalRecipes.map(r => renderRecipeCard(r, true))}
        </div>
      )}
    </div>
  );
};

const MiniMacro = ({ icon: Icon, value, unit, color }: { icon: any; value: string; unit: string; color: string }) => (
  <div className="text-center">
    <Icon className={`w-3 h-3 mx-auto mb-0.5 ${color}`} />
    <p className="text-xs font-bold text-foreground">{value}</p>
    <p className="text-[10px] text-muted-foreground">{unit}</p>
  </div>
);

const VisSwitch = ({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) => (
  <label className="flex items-center gap-2 cursor-pointer">
    <Switch checked={checked} onCheckedChange={onChange} />
    <span className="text-xs text-foreground">{label}</span>
  </label>
);

const ScrollableTitle = ({ name }: { name: string }) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const checkTruncation = useCallback(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, []);

  useEffect(() => {
    checkTruncation();
    window.addEventListener("resize", checkTruncation);
    return () => window.removeEventListener("resize", checkTruncation);
  }, [checkTruncation, name]);

  return (
    <div
      className="overflow-hidden"
      onMouseEnter={() => isTruncated && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={() => isTruncated && setIsHovered(h => !h)}
    >
      <p
        ref={textRef}
        className={`text-sm font-semibold text-foreground whitespace-nowrap ${isHovered ? "" : "truncate"}`}
        title={name}
        style={isHovered ? {
          animation: "marquee 4s linear infinite",
          display: "inline-block",
        } : undefined}
      >
        {name}
      </p>
    </div>
  );
};

export default SavedRecipesList;
